import { useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { LocationDto, ZoneDto } from '@/types/organization';
import type { MaterialDto } from '@/types/materials';
import type { StockTransferLineInput, TransferAvailableStockDto, TransferUnitDto } from '@/types/adjustments';

export type TransferFormLine = StockTransferLineInput & {
  batchNo?: string | null;
  clientName?: string | null;
  materialCode?: string | null;
  fromLocationCode?: string | null;
  availableQty?: number | null;
};

/** A unit in the barcode-wise list, plus whether it has been scanned yet. Scanned units are marked
 *  and float to the top, mirroring the Putaway dialog. */
type UnitRow = TransferUnitDto & { scanned: boolean };

type LocationTypeFilter = 'All' | 'Zone' | 'Rack' | 'Bin';

interface Props {
  fromWarehouseId: string;
  barcodeMandatory: boolean;
  materials: MaterialDto[];
  toLocations: LocationDto[];
  lines: TransferFormLine[];
  setLines: React.Dispatch<React.SetStateAction<TransferFormLine[]>>;
  error: string | null;
  setError: (message: string | null) => void;
}

/**
 * Picks what a transfer moves, out of a chosen source area.
 *
 * The area narrows Warehouse → Zone → Location. Worth knowing about this build's model: a Location
 * is *either* a Rack or a Bin (Location.LocationType) — racks do not contain bins, they are two
 * kinds of the same thing — so there are three levels here, not four. Choosing a Zone covers every
 * rack and bin under it; choosing a Location pins exactly one.
 *
 * Then the two modes:
 *  - Barcode Required OFF: the area's stock is listed material by material with its available
 *    quantity, and the operator types how much of each to move, per row.
 *  - Barcode Required ON:  the operator scans units one by one. Each scan marks its row and lifts it
 *    to the top of the list, and ONE shared destination applies to every marked unit — the same
 *    shape as the Putaway dialog, so an operator moving 30 boxes picks a location once rather than
 *    thirty times. Partial is fine: whatever has been scanned can be submitted.
 */
export default function TransferSourcePicker({
  fromWarehouseId, barcodeMandatory, materials, toLocations, lines, setLines, error, setError,
}: Props) {
  const { t } = useTranslation('stockTransfer');
  const [zoneId, setZoneId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [materialId, setMaterialId] = useState('');

  // Barcode mode only. Barcodes in scan order, newest first — the only scan state that needs storing.
  const [scannedBarcodes, setScannedBarcodes] = useState<string[]>([]);
  const [scanInput, setScanInput] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [destTypeFilter, setDestTypeFilter] = useState<LocationTypeFilter>('All');
  const [destZoneId, setDestZoneId] = useState('');

  const { data: zones = [] } = useQuery({
    queryKey: ['zones', fromWarehouseId],
    queryFn: async () => (await apiClient.get<ZoneDto[]>('/zones', { params: { warehouseId: fromWarehouseId } })).data,
    enabled: Boolean(fromWarehouseId),
  });
  const { data: fromLocations = [] } = useQuery({
    queryKey: ['locations', fromWarehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId: fromWarehouseId } })).data,
    enabled: Boolean(fromWarehouseId),
  });

  // Narrowing the location list to the chosen zone keeps the two selectors consistent — picking a
  // zone then a location from a different zone would silently contradict itself.
  const zoneLocations = useMemo(
    () => (zoneId ? fromLocations.filter((l) => l.zoneId === zoneId) : fromLocations),
    [fromLocations, zoneId],
  );

  const areaParams = {
    warehouseId: fromWarehouseId,
    zoneId: zoneId || undefined,
    locationId: locationId || undefined,
  };

  const { data: available = [], isFetching: stockLoading } = useQuery({
    queryKey: ['transfer-available-stock', fromWarehouseId, zoneId, locationId],
    queryFn: async () => (await apiClient.get<TransferAvailableStockDto[]>('/stock-transfers/available-stock', { params: areaParams })).data,
    enabled: Boolean(fromWarehouseId) && !barcodeMandatory,
  });

  const { data: units = [], isFetching: unitsLoading } = useQuery({
    queryKey: ['transfer-available-units', fromWarehouseId, zoneId, locationId, materialId],
    queryFn: async () => (await apiClient.get<TransferUnitDto[]>('/stock-transfers/available-units', {
      params: { ...areaParams, materialId },
    })).data,
    enabled: Boolean(fromWarehouseId && materialId) && barcodeMandatory,
  });

  // Derived, not stored: keeping a second copy of `units` in state needed an effect to resync it,
  // and that effect fired on every render in quantity-wise mode (where `units` defaults to a fresh
  // []), wiping the operator's typed rows. Only the scan ORDER is state — newest scan first, then
  // everything not yet scanned in server order.
  const unitRows: UnitRow[] = useMemo(() => {
    const scanned = scannedBarcodes
      .map((b) => units.find((u) => u.barcode.toUpperCase() === b))
      .filter((u): u is TransferUnitDto => Boolean(u))
      .map((u) => ({ ...u, scanned: true }));
    const rest = units
      .filter((u) => !scannedBarcodes.includes(u.barcode.toUpperCase()))
      .map((u) => ({ ...u, scanned: false }));
    return [...scanned, ...rest];
  }, [units, scannedBarcodes]);

  const scannedRows = unitRows.filter((r) => r.scanned);

  // Changing the material or the area changes which units exist, so anything already scanned or
  // typed belongs to the previous selection and must go. Done in the handlers rather than an effect:
  // an effect keyed on the fetched arrays re-fires on every render and wipes live input.
  const resetSelection = () => {
    setScannedBarcodes([]);
    setScanInput('');
    setLines([]);
  };

  // The destination list, filtered the same way Putaway filters its putaway target: "All" shows
  // every location, "Rack"/"Bin" narrow by type, "Zone" narrows to one zone's racks and bins.
  const destinationOptions = useMemo(() => {
    const filtered = toLocations.filter((l) => {
      if (destTypeFilter === 'Zone') return destZoneId ? l.zoneId === destZoneId : false;
      return destTypeFilter === 'All' || l.locationType === destTypeFilter;
    });
    return filtered.map((l) => ({ value: l.id, label: `${l.code} (${l.locationType})` }));
  }, [toLocations, destTypeFilter, destZoneId]);

  const destinationZones = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of toLocations) if (l.zoneId) seen.set(l.zoneId, l.zoneName ?? l.zoneId);
    return [...seen].map(([id, name]) => ({ value: id, label: name }));
  }, [toLocations]);

  // One destination for every marked unit — that is the point of the shared selector.
  const syncLinesFor = (barcodes: string[], destination: string) => {
    setLines(barcodes
      .map((b) => units.find((u) => u.barcode.toUpperCase() === b))
      .filter((u): u is TransferUnitDto => Boolean(u))
      .map((r) => ({
      materialId,
      // Server recomputes from the label; sent so the row reads correctly before saving.
      qty: r.qty,
      fromLocationId: r.locationId,
      toLocationId: destination || null,
      batchId: r.batchId,
      batchNo: r.batchNo,
      scannedBarcode: r.barcode,
      materialCode: materials.find((m) => m.id === materialId)?.code ?? null,
      fromLocationCode: r.locationCode,
      availableQty: r.qty,
    })));
  };

  /** Matches a scanned value against a not-yet-scanned row: marks it, lifts it to the top, and
   *  clears the field for the next scan. Runs on every keystroke because a scanner types the whole
   *  value near-instantly, and again on Enter for scanners configured to send one. Mirrors
   *  PutawayPage.tryMatchScan. */
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return false;

    const idx = unitRows.findIndex((r) => !r.scanned && r.barcode.toUpperCase() === trimmed);
    if (idx === -1) {
      // Already-scanned is a distinct mistake from unknown, and worth saying so — an operator
      // scanning the same box twice needs to know the second scan did nothing.
      if (unitRows.some((r) => r.scanned && r.barcode.toUpperCase() === trimmed)) {
        setError(t('form.scanDuplicate'));
        setScanInput('');
        return true;
      }
      return false;
    }

    setError(null);
    const next = [trimmed, ...scannedBarcodes];
    setScannedBarcodes(next);
    syncLinesFor(next, destinationId);
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) setScanInput(value);
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!tryMatchScan(scanInput)) setError(t('form.scanNotInList'));
    }
  };

  // Unticking is the only way back — a mis-scan must be removable without restarting.
  const unmark = (barcode: string) => {
    const next = scannedBarcodes.filter((b) => b !== barcode.toUpperCase());
    setScannedBarcodes(next);
    syncLinesFor(next, destinationId);
  };

  const handleDestinationChange = (value: string) => {
    setDestinationId(value);
    syncLinesFor(scannedBarcodes, value);
  };

  // Narrowing to Rack-only, Bin-only or one zone can invalidate an already-picked destination —
  // clear it rather than leave a selection the filter no longer offers.
  const handleDestTypeFilterChange = (value: LocationTypeFilter | null) => {
    if (!value) return;
    setDestTypeFilter(value);
    if (value !== 'Zone') setDestZoneId('');
    if (value === 'All') return;

    const stillValid = toLocations.some((l) => l.id === destinationId
      && (value === 'Zone' ? l.zoneId === destZoneId : l.locationType === value));
    if (!stillValid) handleDestinationChange('');
  };

  const handleDestZoneChange = (newZoneId: string) => {
    setDestZoneId(newZoneId);
    const stillValid = toLocations.some((l) => l.id === destinationId && l.zoneId === newZoneId);
    if (!stillValid) handleDestinationChange('');
  };

  const keyOfStock = (r: TransferAvailableStockDto) => [r.materialId, r.locationId ?? '', r.batchId ?? ''].join('|');

  const lineForStock = (r: TransferAvailableStockDto) =>
    lines.find((l) => !l.scannedBarcode && l.materialId === r.materialId
      && (l.fromLocationId ?? '') === (r.locationId ?? '') && (l.batchId ?? '') === (r.batchId ?? ''));

  const patchStock = (r: TransferAvailableStockDto, changes: Partial<TransferFormLine>) => {
    setLines((prev) => {
      const idx = prev.findIndex((l) => !l.scannedBarcode && l.materialId === r.materialId
        && (l.fromLocationId ?? '') === (r.locationId ?? '') && (l.batchId ?? '') === (r.batchId ?? ''));
      if (idx === -1) {
        return [...prev, {
          materialId: r.materialId, qty: 0, fromLocationId: r.locationId, toLocationId: null,
          batchId: r.batchId, batchNo: r.batchNo, materialCode: r.materialCode,
          fromLocationCode: r.locationCode, availableQty: r.availableQty, ...changes,
        }];
      }
      return prev.map((l, i) => (i === idx ? { ...l, ...changes } : l));
    });
  };

  const toLocationOptions = toLocations.map((loc) => ({ value: loc.id, label: `${loc.code} (${loc.locationType})` }));

  return (
    <Stack spacing={2}>
      <Alert severity={error ? 'error' : 'info'} onClose={error ? () => setError(null) : undefined}>
        {error ?? (barcodeMandatory ? t('form.barcodeAreaHint') : t('form.areaHint'))}
      </Alert>

      {/* Warehouse → Zone → Location. Zone and Location are both optional: leaving them blank
          transfers from anywhere in the warehouse. */}
      <Stack direction="row" spacing={2}>
        <SearchableSelect
          label={t('form.zone')} value={zoneId} fullWidth
          onChange={(v) => { setZoneId(v); setLocationId(''); resetSelection(); }}
          options={[{ value: '', label: t('form.wholeWarehouse') }, ...zones.map((z) => ({ value: z.id, label: `${z.code} — ${z.name}` }))]}
        />
        <SearchableSelect
          label={t('form.rackOrBin')} value={locationId} fullWidth
          onChange={(v) => { setLocationId(v); resetSelection(); }}
          options={[{ value: '', label: t('form.anyLocation') }, ...zoneLocations.map((l) => ({ value: l.id, label: `${l.code} (${l.locationType})` }))]}
        />
      </Stack>

      {barcodeMandatory ? (
        <>
          <SearchableSelect
            label={t('form.material')} value={materialId} onChange={(v) => { setMaterialId(v); resetSelection(); }} fullWidth
            options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
          />

          {materialId ? (
            <>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <TextField
                  label={t('form.scanBarcode')}
                  value={scanInput}
                  onChange={(e) => handleScanInputChange(e.target.value)}
                  onKeyDown={handleScanKeyDown}
                  helperText={t('form.scanBarcodeHelper')}
                  fullWidth
                  autoFocus
                />
                {/* Live progress — scanning 30 boxes without a counter means counting ticks. */}
                <Chip
                  label={t('form.scannedOfTotal', { scanned: scannedRows.length, total: unitRows.length })}
                  color={scannedRows.length > 0 ? 'success' : 'warning'}
                  sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                />
              </Stack>

              {unitsLoading ? <LinearProgress /> : undefined}
              {!unitsLoading && unitRows.length === 0 ? (
                <Alert severity="warning">{t('form.noUnitsInArea')}</Alert>
              ) : undefined}

              {unitRows.length > 0 ? (
                <Box sx={{ maxHeight: 240, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
                  {unitRows.map((u) => (
                    <Box key={u.barcode} sx={{ py: 0.5, bgcolor: u.scanned ? 'action.hover' : undefined }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Checkbox size="small" checked={u.scanned} onChange={() => u.scanned && unmark(u.barcode)} disabled={!u.scanned} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={u.scanned ? 600 : 400}>{u.barcode}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('form.unitMeta', { qty: u.qty, source: u.source, batch: u.batchNo ?? '—', location: u.locationCode ?? '—' })}
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 0.5 }} />
                    </Box>
                  ))}
                </Box>
              ) : undefined}

              {/* One destination for every scanned unit. Filtered All/Zone/Rack/Bin exactly as the
                  Putaway dialog filters its target, so the two screens behave the same way. */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {t('form.destinationType')}
                </Typography>
                <ToggleButtonGroup
                  color="primary" exclusive size="small" value={destTypeFilter}
                  onChange={(_, value: LocationTypeFilter | null) => handleDestTypeFilterChange(value)}
                >
                  <ToggleButton value="All">{t('form.typeAll')}</ToggleButton>
                  <ToggleButton value="Zone">{t('form.typeZone')}</ToggleButton>
                  <ToggleButton value="Rack">{t('form.typeRack')}</ToggleButton>
                  <ToggleButton value="Bin">{t('form.typeBin')}</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {destTypeFilter === 'Zone' ? (
                <SearchableSelect label={t('form.zone')} value={destZoneId} onChange={handleDestZoneChange} fullWidth options={destinationZones} />
              ) : undefined}

              <SearchableSelect
                label={t('form.destinationForAll')}
                value={destinationId}
                onChange={handleDestinationChange}
                fullWidth
                options={destinationOptions}
                helperText={t('form.destinationForAllHelper')}
              />
            </>
          ) : undefined}
        </>
      ) : (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{t('form.itemsTitle')}</Typography>
          {stockLoading ? <LinearProgress /> : undefined}
          {!stockLoading && available.length === 0 ? (
            <Alert severity="warning">{t('form.noStockInArea')}</Alert>
          ) : undefined}

          {available.length > 0 ? (
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.5 }}>
              {available.map((r) => {
                const line = lineForStock(r);
                return (
                  <Box key={keyOfStock(r)} sx={{ py: 0.75 }}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <Box sx={{ minWidth: 200, flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>{r.materialCode}</Typography>
                          {r.locationType ? <Chip size="small" variant="outlined" label={r.locationType} /> : undefined}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {t('form.stockMeta', {
                            location: r.locationCode ?? '—',
                            zone: r.zoneName ?? '—',
                            batch: r.batchNo ?? '—',
                            available: r.availableQty,
                          })}
                        </Typography>
                      </Box>
                      <TextField
                        type="number" size="small" label={t('form.qty')} sx={{ width: 110 }}
                        value={line?.qty ?? 0}
                        error={(line?.qty ?? 0) > r.availableQty}
                        onChange={(e) => patchStock(r, { qty: Number(e.target.value) })}
                      />
                      <SearchableSelect
                        label={t('form.toLocation')} value={line?.toLocationId ?? ''} size="small"
                        onChange={(v) => patchStock(r, { toLocationId: v || null })}
                        options={toLocationOptions}
                      />
                    </Stack>
                    <Divider sx={{ mt: 0.75 }} />
                  </Box>
                );
              })}
            </Box>
          ) : undefined}
        </Box>
      )}

      <Typography variant="body2" color="text.secondary">
        {t('form.selectedCount', { count: lines.filter((l) => (l.scannedBarcode ? true : l.qty > 0)).length })}
      </Typography>
    </Stack>
  );
}
